/**
 * BootcampOffers.tsx
 *
 * The compact AI Bootcamp band at the top of /pricing (PricingPageExperiment.tsx).
 *
 * WHY THIS EXISTS: on 2026-10-21 Rob found the Bootcamp block too wordy (a
 * payment-plan picker with repeated words, an empty self-paced card) and the
 * monthly plans pushed below the first screen. Research the same day
 * (MarketingManager/docs/investigations/2026-10-21_pricing-page-bootcamp-cta-research.md)
 * found no Bootcamp checkout ever started on /pricing: buyers read the course
 * page first. So each course is one slim row: name, one line, one price line
 * from the catalog, and one primary button to its course page.
 */
import React from 'react';
import { Button, cn } from '../design-system';
import type { CoursePriceSummary } from './pricingEducationPlans';
import './BootcampOffers.css';

export type BootcampOfferPlan = {
  id: string;
  name: string;
  tagline: string;
  cta: string;
  facts?: string[];
  promotionNote?: string;
  checkoutClosedMessage?: string;
  priceSummary?: CoursePriceSummary;
  coursePath?: string;
};

type Props<T extends BootcampOfferPlan> = {
  plans: T[];
  unavailable?: boolean;
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: T) => void | Promise<void>;
  pendingPlanId: string | null;
  /** The page's query string, carried to the course page so UTMs survive. */
  search?: string;
  className?: string;
};

export default function BootcampOffers<T extends BootcampOfferPlan>({
  plans,
  unavailable = false,
  formatPrice,
  onPlanClick,
  pendingPlanId,
  search = '',
  className,
}: Props<T>) {
  if (!plans.length && !unavailable) return null;
  return (
    <section
      className={cn('pricing-bootcamp-offers', className)}
      id="pricing-training"
      aria-label="AI Bootcamp"
    >
      {unavailable ? (
        <p className="pricing-bootcamp-offers__unavailable" role="alert">
          Bootcamp pricing is unavailable right now. Please refresh and try again.
        </p>
      ) : (
        plans.map((plan) => {
          const summary = plan.priceSummary;
          const href = plan.coursePath ? `${plan.coursePath}${search}` : undefined;
          return (
            <article
              className="pricing-bootcamp-offer"
              id={`pricing-plan-${plan.id}`}
              key={plan.id}
            >
              <div className="pricing-bootcamp-offer__copy">
                <h3>{plan.name}</h3>
                <p>{plan.tagline}</p>
                {plan.facts && plan.facts.length > 0 && (
                  <ul className="pricing-bootcamp-offer__facts">
                    {plan.facts.map((fact) => (
                      <li key={fact}>{fact}</li>
                    ))}
                  </ul>
                )}
                {plan.checkoutClosedMessage && (
                  <p className="pricing-bootcamp-offer__status" role="status">
                    {plan.checkoutClosedMessage}
                  </p>
                )}
              </div>
              {summary && (
                <div className="pricing-bootcamp-offer__price">
                  {/* Rob, 2026-10-22: lead with the monthly payment, pay in
                  full underneath. One-payment courses lead with their price. */}
                  <p className="pricing-bootcamp-offer__amount">
                    {summary.originalPrice && (
                      <s className="pricing-bootcamp-offer__was">
                        {formatPrice(summary.originalPrice)}
                      </s>
                    )}
                    <strong>
                      {summary.installments
                        ? `${summary.installments} payments of ${formatPrice(summary.price)}`
                        : formatPrice(summary.price)}
                    </strong>
                  </p>
                  <p className="pricing-bootcamp-offer__terms">
                    {summary.installments && summary.fullPrice
                      ? `or ${formatPrice(summary.fullPrice)} in full`
                      : 'one-time'}
                  </p>
                  {plan.promotionNote && (
                    <p className="pricing-bootcamp-offer__promotion">{plan.promotionNote}</p>
                  )}
                </div>
              )}
              <Button
                className="pricing-bootcamp-offer__cta"
                variant="primary"
                size="lg"
                href={href}
                loading={pendingPlanId === plan.id}
                disabled={Boolean(pendingPlanId) || !href}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  event.preventDefault();
                  if (!pendingPlanId) void onPlanClick(plan);
                }}
              >
                {plan.cta}
              </Button>
            </article>
          );
        })
      )}
    </section>
  );
}
